import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

const API = 'https://api.ctexpresskh.com/api';

export default function CreateShipment() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('manual');
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [excelRows, setExcelRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [trackingExists, setTrackingExists] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const token = localStorage.getItem('token');

  const [form, setForm] = useState({
    tracking_number: '',
    sender_name: 'China Warehouse', sender_phone: '', sender_address: 'China',
    receiver_name: '', receiver_phone: '', receiver_address: '', receiver_customer_code: '',
    goods_description: '', quantity: 1, manual_cbm: '',
    length_cm: '', width_cm: '', height_cm: '', weight_kg: '',
    shipping_method: 'land', note: ''
  });

  useEffect(() => {
    fetch(API + '/rates', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json()).then(d => setRates(d.data || []));
  }, []);

  useEffect(() => {
    if (!searching) return;
    if (!customerSearch || customerSearch.length < 1) { setCustomers([]); setShowDropdown(false); return; }
    const timer = setTimeout(() => {
      fetch(API + '/customers?search=' + encodeURIComponent(customerSearch), { headers: { Authorization: 'Bearer ' + token } })
        .then(r => r.json()).then(d => { setCustomers(d.data || []); setShowDropdown(true); });
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch, searching]);

  const selectCustomer = (c) => {
    setForm(f => ({ ...f, receiver_name: c.name, receiver_phone: c.phone, receiver_address: c.address || '', receiver_customer_code: c.customer_code || '' }));
    setCustomerSearch(c.customer_code + ' - ' + c.name);
    setSearching(false); setShowDropdown(false); setCustomers([]);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Live tracking duplicate check
  React.useEffect(() => {
    const trk = form.tracking_number?.trim();
    if (!trk) { setTrackingExists(false); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(API + '/shipments?search=' + trk, { headers: { Authorization: 'Bearer ' + token } });
        const data = await res.json();
        const exists = (data.data || []).find(s => s.tracking_number === trk);
        setTrackingExists(!!exists);
      } catch {}
    }, 600);
    return () => clearTimeout(timer);
  }, [form.tracking_number, token]);

  // Live auto-calculate
  React.useEffect(() => {
    if (manualMode) return;
    const { length_cm: l, width_cm: w, height_cm: h, weight_kg: kg, shipping_method, quantity } = form;
    if (!l || !w || !h || parseFloat(l)===0 || parseFloat(w)===0 || parseFloat(h)===0) return;
      if (!kg || parseFloat(kg)===0) return;
    const timer = setTimeout(() => {
      const rate = rates.find(r => r.method === shipping_method);
      if (!rate) return;
      const cbm = (parseFloat(l) * parseFloat(w) * parseFloat(h)) / 1000000;
      let type, fee;
      if (cbm >= parseFloat(rate.cbm_threshold)) {
        type = 'CBM';
        fee = Math.max(cbm * parseFloat(rate.rate_per_cbm), (parseFloat(kg) / parseFloat(rate.kg_per_cbm)) * parseFloat(rate.rate_per_cbm));
      } else {
        type = 'EXPRESS';
        fee = Math.max(parseFloat(kg) * parseFloat(rate.rate_per_kg), cbm * parseFloat(rate.rate_per_cbm));
      }
      const qty = parseInt(quantity) || 1;
      setPreview({ cbm: (cbm * qty).toFixed(4), type, fee: (fee * qty).toFixed(2), qty });
    }, 500);
    return () => clearTimeout(timer);
  }, [form.length_cm, form.width_cm, form.height_cm, form.weight_kg, form.shipping_method, form.quantity, rates]);

  const calcPreview = () => {
    const { length_cm: l, width_cm: w, height_cm: h, weight_kg: kg, shipping_method } = form;
    if (!l || !w || !h || !kg) { toast.error('Enter size and weight first'); return; }
    const rate = rates.find(r => r.method === shipping_method);
    if (!rate) return;
    const cbm = (l * w * h) / 1000000;
    let type, fee;
    if (cbm >= parseFloat(rate.cbm_threshold)) {
      type = 'CBM';
      fee = Math.max(cbm * parseFloat(rate.rate_per_cbm), (kg / parseFloat(rate.kg_per_cbm)) * parseFloat(rate.rate_per_cbm));
    } else {
      type = 'EXPRESS';
      fee = Math.max(kg * parseFloat(rate.rate_per_kg), cbm * parseFloat(rate.rate_per_cbm));
    }
    const qty = parseInt(form.quantity) || 1;
    setPreview({ cbm: (cbm * qty).toFixed(4), type, fee: (fee * qty).toFixed(2), qty });
  };

  const submit = async () => {
    if (!form.receiver_name || !form.receiver_phone) { toast.error('Receiver name and phone required'); return; }
    if (trackingExists) { toast.error('Tracking number already exists!'); return; }
    setLoading(true);
    try {
      const res = await fetch(API + '/shipments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) { toast.success('Created! ' + data.data.tracking_number); navigate('/shipments'); }
      else toast.error(data.message || 'Error');
    } catch { toast.error('Server error'); }
    setLoading(false);
  };

  const handleExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws);
        const rows = json.map(row => ({
          tracking_number: (row['Tracking number'] || row['tracking_number'] || row['TrackingNumber'] || '').toString().trim(),
          description: (row['Description'] || row['description'] || '').toString().trim(),
          quantity: parseInt(row['Quantity'] || row['quantity'] || 1),
          weight_kg: parseFloat(row['Weight (kg)'] || row['weight_kg'] || row['Weight'] || 0),
          fee: parseFloat(row['Fee'] || row['fee'] || 0),
          user_id: (row['User ID'] || row['user_id'] || row['UserID'] || '').toString().trim(),
        })).filter(r => r.tracking_number);
        setExcelRows(rows);
        setImportResult(null);
        toast.success(rows.length + ' rows loaded from Excel');
      } catch (err) {
        toast.error('Error reading Excel file');
      }
    };
    reader.readAsBinaryString(file);
  };

  const importExcel = async () => {
    if (excelRows.length === 0) { toast.error('No data to import'); return; }
    setImporting(true);
    try {
      const res = await fetch(API + '/import/excel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ rows: excelRows })
      });
      const data = await res.json();
      if (data.success) {
        setImportResult(data.results);
        toast.success('Done! Created: ' + data.results.created + ', Updated: ' + data.results.updated);
      } else toast.error(data.message || 'Error');
    } catch { toast.error('Server error'); }
    setImporting(false);
  };

  const inp = (label, key, type='text', placeholder='') => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#374151' }}>{label}</label>
      <input type={type} value={form[key]} onChange={e => set(key, e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
    </div>
  );

  return (
    <div style={{ padding: 32, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/shipments')} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' }}>Back</button>
        <h1 style={{ margin: 0, fontSize: 24, color: '#1e293b' }}>Create Shipment</h1>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button onClick={() => { setMode('manual'); setImportResult(null); }}
            style={{ padding: '8px 20px', borderRadius: 8, border: '2px solid', cursor: 'pointer', fontWeight: 600,
              borderColor: mode==='manual' ? '#f97316' : '#e2e8f0', background: mode==='manual' ? '#fff7ed' : '#fff', color: mode==='manual' ? '#f97316' : '#64748b' }}>
            Manual Entry
          </button>
          <button onClick={() => { setMode('excel'); setImportResult(null); }}
            style={{ padding: '8px 20px', borderRadius: 8, border: '2px solid', cursor: 'pointer', fontWeight: 600,
              borderColor: mode==='excel' ? '#10b981' : '#e2e8f0', background: mode==='excel' ? '#d1fae5' : '#fff', color: mode==='excel' ? '#059669' : '#64748b' }}>
            Import Excel
          </button>
        </div>
      </div>

      {mode === 'excel' ? (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 12px', color: '#1e293b' }}>Import from Excel</h3>
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 13, color: '#475569', lineHeight: 1.8 }}>
            <strong>Required columns:</strong> Description, Tracking number, Quantity, Weight (kg), Fee, User ID<br/>
            Same tracking number → updates weight + fee + status to CN Warehouse<br/>
            New tracking number → creates new shipment with status CN Warehouse<br/>
            Unknown User ID → auto-added as Unknown User in customers<br/>
            Tracking numbers are unique — no duplicates allowed
          </div>
          <input type="file" accept=".xlsx,.xls" onChange={handleExcel}
            style={{ display: 'block', marginBottom: 16, padding: '10px', border: '2px dashed #e2e8f0', borderRadius: 8, width: '100%', cursor: 'pointer' }} />

          {excelRows.length > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{excelRows.length} rows ready</span>
                <button onClick={importExcel} disabled={importing}
                  style={{ padding: '10px 28px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: importing?'not-allowed':'pointer', fontWeight: 700, opacity: importing?0.7:1 }}>
                  {importing ? 'Importing...' : 'Import Now'}
                </button>
              </div>
              <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                    <tr>{['#','Tracking','Description','Qty','Weight','Fee','User ID'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {excelRows.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '7px 12px', color: '#94a3b8' }}>{i+1}</td>
                        <td style={{ padding: '7px 12px', fontWeight: 700, color: '#f97316' }}>{r.tracking_number}</td>
                        <td style={{ padding: '7px 12px' }}>{r.description}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'center' }}>{r.quantity}</td>
                        <td style={{ padding: '7px 12px' }}>{r.weight_kg}kg</td>
                        <td style={{ padding: '7px 12px', fontWeight: 600, color: '#059669' }}>${r.fee}</td>
                        <td style={{ padding: '7px 12px', color: '#64748b', fontSize: 12 }}>{r.user_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {importResult && (
            <div style={{ marginTop: 20, background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 10, padding: 20 }}>
              <div style={{ fontWeight: 700, color: '#059669', fontSize: 16, marginBottom: 10 }}>Import Complete!</div>
              <div style={{ fontSize: 14, color: '#065f46', lineHeight: 2 }}>
                <div>Created: <strong>{importResult.created}</strong> new shipments</div>
                <div>Updated: <strong>{importResult.updated}</strong> existing shipments to CN WH</div>
                {importResult.new_customers > 0 && <div>Unknown users added: <strong>{importResult.new_customers}</strong></div>}
                {importResult.errors.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ color: '#dc2626', fontWeight: 700, marginBottom: 6 }}>⚠️ Errors ({importResult.errors.length}):</div>
                    <div style={{ background: '#fee2e2', borderRadius: 8, padding: 10, maxHeight: 200, overflowY: 'auto' }}>
                      {importResult.errors.map((err, i) => (
                        <div key={i} style={{ fontSize: 12, color: '#dc2626', padding: '3px 0', borderBottom: '1px solid #fecaca' }}>
                          {i+1}. {err}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button onClick={() => navigate('/shipments')} style={{ marginTop: 12, padding: '10px 24px', background: '#059669', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>
                View Shipments
              </button>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: 16, marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 700, color: '#92400e' }}>Tracking Number (Manual)</label>
            <input value={form.tracking_number} onChange={e => set('tracking_number', e.target.value.toUpperCase())}
              placeholder="Leave empty to auto-generate or enter custom tracking number"
              style={{ width: '100%', padding: '10px 14px', border: trackingExists ? '2px solid #dc2626' : '2px solid #fed7aa', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#fffbf5' }} />
            {trackingExists && <div style={{ color:'#dc2626', fontSize:12, marginTop:4, fontWeight:600 }}>⚠️ Tracking number already exists!</div>}
            <div style={{ fontSize: 12, color: '#92400e', marginTop: 4 }}>Leave empty to auto-generate. Each tracking number must be unique.</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <h3 style={{ margin: '0 0 16px', color: '#f97316' }}>Sender (China)</h3>
              {inp('Sender Name', 'sender_name')}
              {inp('Sender Phone', 'sender_phone', 'text', 'Optional')}
              {inp('Sender Address', 'sender_address')}
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <h3 style={{ margin: '0 0 16px', color: '#f97316' }}>Receiver (Cambodia)</h3>
              <div style={{ marginBottom: 16, position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#374151' }}>Search Customer (ID / Name / Phone)</label>
                <input value={customerSearch} onChange={e => { setCustomerSearch(e.target.value); setSearching(true); setShowDropdown(false); }}
                  placeholder="Type to search..."
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                {showDropdown && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: 220, overflowY: 'auto' }}>
                    {customers.length === 0 ? <div style={{ padding: 12, fontSize: 14, color: '#94a3b8' }}>No customer found</div>
                      : customers.map(c => (
                      <div key={c.id} onMouseDown={() => selectCustomer(c)}
                        style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: 14 }}
                        onMouseEnter={e => e.currentTarget.style.background='#fff7ed'}
                        onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                        <span style={{ fontWeight: 700, color: '#f97316' }}>{c.customer_code}</span>
                        <span style={{ marginLeft: 8 }}>{c.name}</span>
                        <span style={{ color: '#94a3b8', marginLeft: 8, fontSize: 12 }}>{c.phone}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {inp('Receiver Name', 'receiver_name', 'text', 'Full name')}
              {inp('Receiver Phone', 'receiver_phone', 'text', 'Phone number')}
              {inp('Receiver Address', 'receiver_address', 'text', 'Address in Cambodia')}
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <h3 style={{ margin: '0 0 16px', color: '#f97316' }}>Goods Info</h3>
              {inp('Description', 'goods_description', 'text', 'e.g. Electronics, Clothes')}
              {inp('Quantity', 'quantity', 'number', '1')}
              {/* Mode Toggle */}
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, background:'#f8fafc', borderRadius:8, padding:'10px 14px' }}>
                <span style={{ fontSize:13, fontWeight:600, color: manualMode ? '#94a3b8' : '#f97316' }}>🔢 Auto Calculate</span>
                <div onClick={() => { setManualMode(!manualMode); setPreview(null); }}
                  style={{ width:44, height:24, borderRadius:12, background: manualMode ? '#f97316' : '#e2e8f0', cursor:'pointer', position:'relative', transition:'background 0.2s' }}>
                  <div style={{ width:18, height:18, borderRadius:9, background:'#fff', position:'absolute', top:3, left: manualMode ? 23 : 3, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
                <span style={{ fontSize:13, fontWeight:600, color: manualMode ? '#f97316' : '#94a3b8' }}>✏️ Manual Input</span>
              </div>

              {manualMode && (
                <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:8, padding:12, marginBottom:12 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#92400e', marginBottom:10 }}>✏️ Manual Input — enter values directly</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <div>
                      <label style={{ display:'block', marginBottom:4, fontSize:12, fontWeight:600, color:'#374151' }}>Total CBM (m³)</label>
                      <input type="number" value={form.manual_cbm || ''} onChange={e => set('manual_cbm', e.target.value)}
                        placeholder="e.g. 3.5 total CBM"
                        style={{ width:'100%', padding:'8px 10px', border:'1px solid #d1d5db', borderRadius:7, fontSize:13, boxSizing:'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display:'block', marginBottom:4, fontSize:12, fontWeight:600, color:'#374151' }}>Fee ($) total</label>
                      <input type="number" value={form.shipping_fee || ''} onChange={e => set('shipping_fee', e.target.value)}
                        placeholder="e.g. 25.00"
                        style={{ width:'100%', padding:'8px 10px', border:'1px solid #d1d5db', borderRadius:7, fontSize:13, boxSizing:'border-box' }} />
                    </div>
                  </div>
                  {form.manual_cbm && form.quantity > 1 && (
                    <div style={{ fontSize:12, color:'#92400e', marginTop:8 }}>
                      Total CBM: {(parseFloat(form.manual_cbm||0)*(parseInt(form.quantity)||1)).toFixed(4)} m³ ({form.manual_cbm} × {form.quantity})
                    </div>
                  )}
                </div>
              )}
              {inp('Note', 'note', 'text', 'Optional note')}
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <h3 style={{ margin: '0 0 16px', color: '#f97316' }}>Size & Fee</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
                {[['L (cm)','length_cm'],['W (cm)','width_cm'],['H (cm)','height_cm']].map(([label,key]) => (
                  <div key={key}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#374151' }}>{label}</label>
                    <input type="number" value={form[key]} onChange={e => set(key, e.target.value)}
                      style={{ width: '100%', padding: '9px 8px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                  </div>
                ))}
              </div>
              {inp('Weight (kg)', 'weight_kg', 'number', '0')}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#374151' }}>Shipping Method</label>
                <select value={form.shipping_method} onChange={e => set('shipping_method', e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
                  <option value="sea">By Sea</option>
                  <option value="land">By Land</option>
                  <option value="air">By Air</option>
                </select>
              </div>
              <button onClick={calcPreview} style={{ width: '100%', padding: 10, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontWeight: 600, marginBottom: 12 }}>
                Calculate Fee
              </button>
              {preview && (
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <span style={{ color: '#92400e' }}>CBM</span><strong>{preview.cbm} m3</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
                    <span style={{ color: '#92400e' }}>Type</span>
                    <strong style={{ color: preview.type==='CBM'?'#1d4ed8':'#b45309' }}>{preview.type}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#92400e', fontWeight: 600 }}>Fee</span>
                    <span style={{ fontSize: 22, fontWeight: 'bold', color: '#f97316' }}>${preview.fee}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button onClick={() => navigate('/shipments')} style={{ padding: '10px 24px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: '#fff' }}>Cancel</button>
            <button onClick={submit} disabled={loading || trackingExists} style={{ padding: '10px 32px', background: '#f97316', color: '#fff', border: 'none', borderRadius: 8, cursor: loading?'not-allowed':'pointer', fontWeight: 700, opacity: loading?0.7:1 }}>
              {loading ? 'Creating...' : 'Create Shipment'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
